/*
 * Dateibibliothek eines Boards (0.54): Ordner und dauerhaft abgelegte Dateien, unabhängig von Karten.
 * Kartenanhänge aus der Bibliothek teilen die Binärdatei; der Bibliothekseintrag kann umbenannt,
 * verschoben oder gelöscht werden, ohne dass sich vorhandene Anhänge ändern.
 */
import { z } from 'zod';
import type { MediaKind } from './exports.js';

export const LIBRARY_MAX_DEPTH = 4;
export const LIBRARY_NAME_MAX = 120;
/** 0.55: Dateien im Papierkorb werden nach so vielen Tagen endgültig gelöscht */
export const LIBRARY_TRASH_DAYS = 30;
/** 0.55: höchstens so viele Dateien je Sammelaktion bzw. ZIP */
export const LIBRARY_BULK_MAX = 200;

/**
 * 0.57 Team-Bibliothek: Ordner der obersten Ebene werden für Boards des Teams freigegeben –
 * für alle (`all`) oder ausgewählte (`boards`). Ohne Freigabe sehen nur Teammitglieder den Ordner.
 */
export type LibraryPublishMode = 'none' | 'all' | 'boards';
export const LIBRARY_PUBLISH_TEXT: Record<LibraryPublishMode, string> = {
  none: 'Nur im Team',
  all: 'Alle Boards des Teams',
  boards: 'Ausgewählte Boards',
};
/** Wer die Team-Bibliothek ändern darf */
export type LibraryTeamEdit = 'admins' | 'members';

export interface LibraryFolder {
  id: string;
  parentId: string | null;
  name: string;
  /** 0.57: Freigabe (nur Team-Bibliothek, oberste Ebene, nur für Teammitglieder sichtbar) */
  publish?: { mode: LibraryPublishMode; boardIds: string[] };
  /** Dateien direkt darin (ohne Papierkorb) und Unterordner */
  files: number;
  folders: number;
}

export interface LibraryFile {
  id: string;
  folderId: string | null;
  name: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  isImage: boolean;
  url: string;
  thumbUrl: string | null;
  previewUrl: string | null;
  previewWidth: number | null;
  placeholder: string | null;
  uploaderName: string | null;
  createdAt: string;
  updatedAt: string;
  trashedAt: string | null;
  /** 0.55: Zeitpunkt der automatischen Löschung (nur im Papierkorb) */
  purgeAt: string | null;
  /** 0.55: Nummer der aktuellen Fassung (1 = nie ersetzt) */
  version: number;
  /** Anzahl der Kartenanhänge mit dieser Datei */
  usage: number;
  /** Ordnerpfad (nur bei Suche über Unterordner) */
  path?: string;
}

export interface LibraryListing {
  /** Pfad bis zum aktuellen Ordner (leer = oberste Ebene) */
  path: { id: string; name: string }[];
  folders: LibraryFolder[];
  files: LibraryFile[];
  total: number;
  /** 0.57: darf die betrachtende Person hier ändern bzw. freigeben (Team-Bibliothek) */
  canEdit?: boolean;
  canPublish?: boolean;
  /** 0.57: Team-Bibliothek – Name und Bearbeitungsrecht */
  teamName?: string;
  teamEdit?: LibraryTeamEdit;
  /** 0.57: Board-Ansicht der Team-Bibliothek – ist die Person Mitglied des Teams */
  teamMember?: boolean;
}

/** 0.57: Boards eines Teams für die Freigabe (nur sichtbare) */
export interface LibraryTeamBoard {
  id: string;
  title: string;
}

export interface LibraryUsage {
  /** `current`: der Anhang zeigt auf die aktuelle Fassung; sonst eine ältere (Nummer in `version`, falls bekannt) */
  cards: { id: string; title: string; archived: boolean; attachmentId: string; current: boolean; version: number | null; boardId: string; boardTitle: string }[];
}

/** 0.55: Fassungen einer Bibliotheksdatei – die aktuelle zuerst */
export interface LibraryVersion {
  /** ID der Fassung; bei der aktuellen Fassung die Datei-ID */
  id: string;
  version: number;
  current: boolean;
  name: string;
  mime: string;
  size: number;
  isImage: boolean;
  url: string;
  thumbUrl: string | null;
  previewUrl: string | null;
  previewWidth: number | null;
  width: number | null;
  height: number | null;
  downloadUrl: string;
  uploaderName: string | null;
  createdAt: string;
  /** Anhänge in Karten mit genau dieser Fassung */
  usage: number;
}

const name = z.string().trim().min(1, 'Bitte einen Namen angeben.').max(LIBRARY_NAME_MAX);
export const libraryFolderSchema = z.object({ name, parentId: z.string().uuid().nullable().default(null) });
export const libraryFolderPatchSchema = z.object({ name: name.optional(), parentId: z.string().uuid().nullable().optional() });
export const libraryFilePatchSchema = z.object({ name: name.optional(), folderId: z.string().uuid().nullable().optional() });
/** `teamId` (0.58): in die Team-Bibliothek des Boards ablegen statt in die Board-Bibliothek */
export const libraryToSchema = z.object({ folderId: z.string().uuid().nullable().default(null), name: name.optional(), teamId: z.string().uuid().optional() });
export const libraryUseSchema = z.object({ fileId: z.string().uuid(), asCover: z.boolean().default(false) });

export const libraryBulkSchema = z.object({
  action: z.enum(['move', 'trash', 'restore', 'delete']),
  fileIds: z.array(z.string().uuid()).min(1).max(LIBRARY_BULK_MAX),
  folderId: z.string().uuid().nullable().optional(),
});
export type LibraryBulk = z.infer<typeof libraryBulkSchema>;
export const libraryUpdateAttachmentsSchema = z.object({ attachmentIds: z.array(z.string().uuid()).max(500).optional() });

export const libraryPublishSchema = z.object({
  mode: z.enum(['none', 'all', 'boards']),
  boardIds: z.array(z.string().uuid()).max(500).default([]),
});
export const libraryTeamSettingsSchema = z.object({ edit: z.enum(['admins', 'members']) });

export type LibraryKind = MediaKind;
